import {
  DocumentReference,
  isDocRef,
  ISelectOption,
  isObject,
  Timestamp,
  TypeGuard,
} from '@principle-theorem/shared';
import { IInvoice } from '../../../invoice/invoice';
import { AccPatientFormType } from '../../../patient/patient';
import { AccOccupationCode } from '../../acc/acc-code-tables';
import {
  IAccClaim,
  IAccClaimStatus,
  isValidAccClaim,
} from '../../acc/claim/acc-claim';
import { IAccDocumentMetadata } from '../../acc/document/acc-document';
import {
  FundingRequestProvider,
  FundingRequestType,
  IFundingRequest,
  isFundingRequest,
} from '../funding-request';
import { isBoolean } from 'lodash';

export interface IAccClaimDocument {
  path: string;
  sentAt: Timestamp;
  metadata: IAccDocumentMetadata;
}

export interface IAccClaimFundingRequestData {
  claimData: IAccClaim;
  metadata: IAccClaimFormMetadata;
  invoiceRefs?: DocumentReference<IInvoice>[];
  claimStatus?: IAccClaimStatus;
  documents?: IAccClaimDocument[];
  testMode?: boolean;
}

export const isAccClaimFundingRequestData =
  TypeGuard.interface<IAccClaimFundingRequestData>({
    claimData: isValidAccClaim,
    metadata: isObject,
    invoiceRefs: TypeGuard.undefinedOr(TypeGuard.arrayOf(isDocRef)),
    claimStatus: TypeGuard.undefinedOr(isObject),
    documents: TypeGuard.undefinedOr(TypeGuard.arrayOf(isObject)),
    testMode: TypeGuard.undefinedOr(isBoolean),
  });

export type IAccClaimFundingRequest =
  IFundingRequest<IAccClaimFundingRequestData>;

export function isAccClaimFundingRequest(
  item: unknown
): item is IAccClaimFundingRequest {
  return (
    isFundingRequest(item) &&
    isAccClaimFundingRequestData(item.data) &&
    item.fundingProvider === FundingRequestProvider.ACC &&
    item.type === FundingRequestType.PreApproval
  );
}

export interface IAccClaimFormMetadata {
  occupationCode?: ISelectOption<AccOccupationCode>;
  includeReferrals?: boolean;
  patientForms?: Record<AccPatientFormType, IAccPatientFormTracking>;
}

export interface IAccPatientFormTracking {
  selected: boolean;
  issuedAt?: Timestamp;
  submittedAt?: Timestamp;
}

export enum AccPatientCommunicationType {
  SMS = 'SMS',
  Email = 'Email',
}

export const ACC_PATIENT_FORM_STEP_MAP: Partial<
  Record<AccPatientFormType, number>
> = {
  [AccPatientFormType.AccPatientDetailsForm]: 1,
  [AccPatientFormType.AccAccidentDetailsForm]: 2,
  [AccPatientFormType.AccEmploymentDetailsForm]: 3,
};
